'use client';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { getMediaGalleryItemService, getMediaGalleryListService } from '@/services/httpServices/MediaGalleryServices';
import type {
  GetMediaGalleryItemServiceConfig,
  MediaGalleryGetResponse,
  MediaGalleryListResponse,
  MediaGalleryListServiceConfig,
} from '@/services/httpServices/MediaGalleryServices/types';

import { mediaGalleryItemKeys, mediaGalleryListKeys } from './mediaGalleryQueryKeys';
import { useQueryConfig } from './useQueryConfig';

/**
 * Checks if any media gallery item has a "processing" status
 * Used to determine if automatic refetching should be enabled for the gallery list query
 * @param pages - Array of pages from the infinite query
 * @returns True if any item has "processing" status, false otherwise
 */
function hasProcessingItems(pages: MediaGalleryListResponse[] | undefined): boolean {
  if (pages === undefined || pages.length === 0) {
    return false;
  }

  return pages.some((page) => {
    return page.nodes.some((node) => {
      // Check if any image in the group has a processing status
      return node.images.some(image => image.status === 'processing');
    });
  });
}

/**
 * Checks if a single media gallery item has a "processing" status
 * Used to determine if automatic refetching should be enabled for the gallery item query
 * @param data - Media gallery item data
 * @returns True if the item has "processing" status, false otherwise
 */
function isItemProcessing(data: MediaGalleryGetResponse | undefined): boolean {
  if (data === undefined) {
    return false;
  }

  return data.data.status === 'processing';
}

/**
 * Hook to fetch media gallery list with infinite scrolling using cursor-based pagination
 * @param initialParams - Media gallery list query parameters
 * @returns Infinite query result for the media gallery list
 */
export function useInfiniteMediaGalleryListQuery(initialParams: MediaGalleryListServiceConfig) {
  const { isEnabled } = useQueryConfig();

  return useInfiniteQuery<MediaGalleryListResponse>({
    queryKey: mediaGalleryListKeys(initialParams),
    queryFn: async ({ pageParam }) => {
      const paginationParams: MediaGalleryListServiceConfig = {
        ...initialParams,
        params: {
          ...initialParams.params,
          query: {
            ...initialParams.params?.query,
            paginationAfter: pageParam ? String(pageParam) : undefined,
          },
        },
      };

      return getMediaGalleryListService(paginationParams);
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      if (lastPage.pageInfo.hasNextPage === true && lastPage.pageInfo.endCursor !== null) {
        return lastPage.pageInfo.endCursor;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.pageInfo.hasPreviousPage === true && firstPage.pageInfo.startCursor !== null) {
        return firstPage.pageInfo.startCursor;
      }
      return undefined;
    },
    refetchInterval: (query) => {
      return hasProcessingItems(query.state.data?.pages) ? 5000 : false;
    },
    enabled: isEnabled,
  });
}

/**
 * Hook to fetch a single media gallery item by ID
 * @param params - Media gallery item query parameters with id
 * @returns Query result for the media gallery item
 */
export function useMediaGalleryItemQuery(params: GetMediaGalleryItemServiceConfig) {
  const { isEnabled } = useQueryConfig();

  return useQuery<MediaGalleryGetResponse>({
    queryKey: mediaGalleryItemKeys(params),
    queryFn: () => getMediaGalleryItemService(params),
    refetchInterval: (query) => {
      return isItemProcessing(query.state.data) ? 5000 : false;
    },
    enabled: isEnabled && Boolean(params?.params?.params?.id),
  });
}
